import React, { useState, useMemo } from 'react'
import { AccessLog } from '../MetricasEmpresasPage'
import '../styles/logsTable.css'

interface LogsTableProps {
    logs: AccessLog[]
}

function LogsTable({ logs }: LogsTableProps) {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [sortConfig, setSortConfig] = useState<{
        key: keyof AccessLog
        direction: 'asc' | 'desc'
    } | null>(null)

    const filteredAndSortedLogs = useMemo(() => {
        let filtered = logs

        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase()
            filtered = logs.filter((log) =>
                Object.values(log).some((value) =>
                    String(value).toLowerCase().includes(lowerSearchTerm)
                )
            )
        }

        if (sortConfig) {
            filtered = [...filtered].sort((a, b) => {
                const aValue = String(a[sortConfig.key]).toLowerCase()
                const bValue = String(b[sortConfig.key]).toLowerCase()

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1
                }
                return 0
            })
        }

        return filtered
    }, [logs, searchTerm, sortConfig])

    const handleSort = (key: keyof AccessLog) => {
        let direction: 'asc' | 'desc' = 'asc'
        if (
            sortConfig &&
            sortConfig.key === key &&
            sortConfig.direction === 'asc'
        ) {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    const getStatusClass = (status: string) => {
        if (status.startsWith('2')) return 'status_success'
        if (status.startsWith('3')) return 'status_redirect'
        if (status.startsWith('4')) return 'status_client_error'
        if (status.startsWith('5')) return 'status_server_error'
        return ''
    }

    const getMethodClass = (method: string) => {
        const classMap: { [key: string]: string } = {
            GET: 'method_get',
            POST: 'method_post',
            PUT: 'method_put',
            DELETE: 'method_delete',
            PATCH: 'method_patch',
            HEAD: 'method_head',
            OPTIONS: 'method_options'
        }
        return classMap[method] || 'method_other'
    }

    const columns: { key: keyof AccessLog; label: string }[] = [
        { key: 'ip', label: 'IP' },
        { key: 'data', label: 'Data/Hora' },
        { key: 'method', label: 'Método' },
        { key: 'rota', label: 'Rota' },
        { key: 'status', label: 'Status' },
        { key: 'responseTime', label: 'Tempo (ms)' },
        { key: 'tz', label: 'TZ' },
        { key: 'origin', label: 'Origin' },
        { key: 'client', label: 'Cliente' }
    ]

    return (
        <div className='logs_table_container'>
            <div className='table_header'>
                <h3>Logs de Acesso ({filteredAndSortedLogs.length} registros)</h3>
                <div className='search_box'>
                    <span className='material-symbols-outlined'>search</span>
                    <input
                        type='text'
                        placeholder='Pesquisar em todos os campos...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className='search_input'
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className='clear_search'
                            title='Limpar pesquisa'
                        >
                            <span className='material-symbols-outlined'>close</span>
                        </button>
                    )}
                </div>
            </div>

            <div className='table_wrapper'>
                <table className='logs_table'>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} onClick={() => handleSort(col.key)}>
                                    <div className='table_header_cell'>
                                        <span>{col.label}</span>
                                        {sortConfig?.key === col.key && (
                                            <span className='sort_indicator'>
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAndSortedLogs.length > 0 ? (
                            filteredAndSortedLogs.map((log, idx) => (
                                <tr key={idx} className='log_row'>
                                    <td className='cell_ip'>{log.ip}</td>
                                    <td className='cell_data'>
                                        {new Date(log.data).toLocaleString('pt-BR')}
                                    </td>
                                    <td className={`cell_method ${getMethodClass(log.method)}`}>
                                        <span className='badge'>{log.method}</span>
                                    </td>
                                    <td className='cell_rota' title={log.rota}>
                                        {log.rota}
                                    </td>
                                    <td className={`cell_status ${getStatusClass(log.status)}`}>
                                        <span className='badge'>{log.status}</span>
                                    </td>
                                    <td className='cell_response_time'>
                                        <span
                                            className={`response_time ${
                                                parseInt(log.responseTime) > 1000 ? 'slow' : ''
                                            }`}
                                        >
                                            {log.responseTime}
                                        </span>
                                    </td>
                                    <td className='cell_tz'>{log.tz}</td>
                                    <td className='cell_origin' title={log.origin}>
                                        {log.origin || '-'}
                                    </td>
                                    <td className='cell_client' title={log.client}>
                                        {log.client || '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr className='no_data_row'>
                                <td colSpan={columns.length} className='no_data_cell'>
                                    Nenhum registro encontrado
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default LogsTable
